// /app/frontend/src/components/frugal-v2/sections/ContingencySection.jsx
// Vital Reserve section - shows vital budget, spent and remaining with category breakdown

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Shield, AlertTriangle, ChevronDown, ChevronRight, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { translateCategory } from '../constants/categories';

const ContingencySection = ({ contingency, zen, vitalTransactions = [], fundingStatus, onEditVital, onDeleteVital, t }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [menuOpen, setMenuOpen] = useState(null); // { id, x, y }

  // Close menu on outside click / escape
  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => {
      if (e.type === 'keydown' && e.key !== 'Escape') return;
      setMenuOpen(null);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [menuOpen]);
  
  // Use vital data from zen.totals
  const totals = zen?.totals || {};
  const total_budget = totals.vital_budget || 0;
  const overflow = zen?.contingency_overflow || 0;
  // Effective funded = budget reduced by contingency overflow that eats into vital.
  // This makes the displayed math intuitive: Funded - Used = Remaining.
  const vital_funded = Math.max(0, (totals.vital_funded ?? total_budget) - overflow);
  const total_used = totals.vital_spent || 0;
  const remaining = totals.vital_remaining || 0;
  
  const percentRemaining = vital_funded > 0 ? (remaining / vital_funded * 100) : 100;
  const hasAlert = percentRemaining < 30;

  // Backend already routed expenses based on user_vital_set (defaults + flagged).
  // Trust the server: render every tx received in vitalTransactions.
  const vitalExpenses = vitalTransactions;
  
  const groupedByCategory = vitalExpenses.reduce((acc, tx) => {
    const cat = tx.category || 'Otros';
    if (!acc[cat]) acc[cat] = { items: [], total: 0 };
    acc[cat].items.push(tx);
    acc[cat].total += tx.amount || 0;
    return acc;
  }, {});

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  // fundingStatus takes priority over hasAlert
  const useAlert = hasAlert && !fundingStatus;

  const fundingBorder = fundingStatus === 'funded' ? 'border-emerald-500/40'
    : fundingStatus === 'partial' ? 'border-amber-500/40'
    : fundingStatus === 'none' ? 'border-white/10'
    : useAlert ? 'border-red-500/30' : 'border-white/10';
  const fundingTitle = fundingStatus === 'funded' ? 'text-emerald-400'
    : fundingStatus === 'partial' ? 'text-amber-400'
    : fundingStatus === 'none' ? 'text-slate-400'
    : useAlert ? 'text-red-400' : 'text-purple-400';
  const fundingBg = useAlert ? 'bg-red-500/10' : 'bg-white/5';

  return (
    <div className={`rounded-xl border overflow-hidden ${fundingBg} ${fundingBorder}`}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 text-left"
      >
        {/* Line 1: Title + Remaining */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className={`w-5 h-5 ${fundingTitle}`} />
            <span className={`font-semibold ${fundingTitle}`}>
              {t('frugal.contingency.title')}
            </span>
            {useAlert && <AlertTriangle className="w-4 h-4 text-red-400" />}
          </div>
          <div className="flex items-center gap-2">
            <span className="font-bold text-lg text-yellow-400">
              ${remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </div>
        </div>
        
        {/* Line 2: Budgeted + Funded + Used */}
        <div className="flex justify-end mt-1">
          <span className="text-xs text-slate-400">
            {t('frugal.contingency.budgeted', 'Presupuestado')} ${total_budget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} • {t('frugal.contingency.funded', 'Fondeado')} ${vital_funded.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} • {t('frugal.contingency.used')} ${total_used.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
      </button>

      {/* Expandable content - Vital Categories Breakdown */}
      {isExpanded && (
        <div className="border-t border-white/10 p-4 space-y-2">
          {/* Warning when depleted */}
          {percentRemaining <= 0 && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <div className="text-sm text-red-400">
                {t('frugal.contingency.depletedWarning')}
              </div>
            </div>
          )}

          {/* Vital Categories */}
          {Object.keys(groupedByCategory).length === 0 ? (
            <div className="text-center py-4 text-slate-400 text-sm">
              {t('frugal.vitalExpenses.empty') || 'No vital expenses this month'}
            </div>
          ) : (
            Object.entries(groupedByCategory).map(([category, data]) => {
              const isCatExpanded = expandedCategories[category];
              
              return (
                <div key={category} className="bg-white/5 rounded-lg overflow-hidden">
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      {isCatExpanded ? (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      )}
                      <span className="text-white font-medium">
                        {translateCategory(t, category)}
                      </span>
                      <span className="text-slate-500 text-sm">({data.items.length})</span>
                    </div>
                    <span className="text-red-400 font-semibold">
                      ${data.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </button>

                  {/* Category Items */}
                  {isCatExpanded && (
                    <div className="border-t border-white/5 px-3 py-2 space-y-1">
                      {data.items.map(item => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between py-2 text-sm group"
                        >
                          <span className="text-slate-300">{item.name || item.description}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-slate-400">
                              ${item.amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (menuOpen?.id === item.id) {
                                    setMenuOpen(null);
                                  } else {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const menuHeight = 88; // ~2 items * 40px + padding
                                    const spaceBelow = window.innerHeight - rect.bottom;
                                    const openUp = spaceBelow < menuHeight + 16;
                                    setMenuOpen({
                                      id: item.id,
                                      x: rect.right - 140, // align right edge with min-w 140
                                      y: openUp ? rect.top - menuHeight - 4 : rect.bottom + 4
                                    });
                                  }
                                }}
                                className="p-1 rounded hover:bg-white/10 text-slate-500 hover:text-white transition-colors"
                                data-testid={`vital-tx-menu-${item.id}`}
                              >
                                <MoreVertical className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Portal Menu - rendered outside the overflow-hidden container */}
      {menuOpen && (() => {
        const item = vitalExpenses.find(x => x.id === menuOpen.id);
        if (!item) return null;
        return ReactDOM.createPortal(
          <div
            className="fixed z-[9999] bg-slate-800 border border-white/10 rounded-lg shadow-xl py-1 w-[140px]"
            style={{ left: Math.max(8, menuOpen.x), top: menuOpen.y }}
            onMouseDown={(e) => e.stopPropagation()}
            data-testid="vital-tx-menu-portal"
          >
            {onEditVital && (
              <button
                onClick={() => { setMenuOpen(null); onEditVital(item); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-white/10"
              >
                <Pencil className="w-3 h-3" /> {t('frugal.actions.edit', 'Editar')}
              </button>
            )}
            {onDeleteVital && (
              <button
                onClick={() => { setMenuOpen(null); onDeleteVital(item.id); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-3 h-3" /> {t('frugal.actions.delete', 'Borrar')}
              </button>
            )}
          </div>,
          document.body
        );
      })()}
    </div>
  );
};

export default ContingencySection;
